import React, { useState, useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';
import classnames from 'classnames';
import { makeStyles } from '@fluentui/react-components';
import { ItemPlaceholderBoxView } from './item-placeholder-box-view';
import { VideoPlaybackView } from './video-playback-view';


const videoIcon = "data:image/svg+xml,%3Csvg width='14' height='12' viewBox='0 0 14 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M1 3C1 2.17157 1.67157 1.5 2.5 1.5H8.5C9.32843 1.5 10 2.17157 10 3V9C10 9.82843 9.32843 10.5 8.5 10.5H2.5C1.67157 10.5 1 9.82843 1 9V3ZM2.5 0.5C1.11929 0.5 0 1.61929 0 3V9C0 10.3807 1.11929 11.5 2.5 11.5H8.5C9.88071 11.5 11 10.3807 11 9V8.49943L12.7999 9.84997C13.2943 10.221 14 9.86819 14 9.25008V2.75496C14 2.13743 13.2954 1.78451 12.8009 2.15432L11 3.50093V3C11 1.61929 9.88071 0.5 8.5 0.5H2.5ZM11 4.74959L13 3.25407V8.74993L11 7.24921V4.74959Z' fill='white'/%3E%3C/svg%3E%0A";

const VideoLoadState = {
  notLoaded: 'notLoaded',
  loaded: 'loaded',
  error: 'error',
};

const useStyles = makeStyles({
  container: {
    marginTop: '20px',
  },
  placeholderView: {
    height: '500px',
    maxWidth: '746px',
    width: '100%',
  },
  hidden: {
    visibility: 'hidden',
  },
  shown: {
    visibility: 'visible',
  },
});

const PlaceHolderBoxViewFooterConfig = {
  text: {
    summary: _TL_('Your video is currently loading.'),
    tip: _TL_('As long as your browser supports the video\'s URL, it\'ll display once it finishes.'),
  },
};

export const VideoLoadPreview = ({
  i18n,
  url,
  onLoadedMetadata,
  onErrorHandler,
}) => {
  const classes = useStyles();
  const [videoLoadState, setVideoLoadState] = useState(VideoLoadState.notLoaded);
  const [cachedUrl, setCachedUrl] = useState(url);

  useEffect(() => {
    if (url !== cachedUrl) {
      setCachedUrl(url);
      setVideoLoadState(VideoLoadState.notLoaded);
    }
  }, [cachedUrl, url]);

  const footerProps = useMemo(() => {
    const localizedTextFields = _.mapObject(
      PlaceHolderBoxViewFooterConfig.text,
      text => i18n.getString(text)
    );

    return {
      ...localizedTextFields,
    };
  }, [i18n]);

  const onLoadedMetadataInternal = useCallback(() => {
    setVideoLoadState(VideoLoadState.loaded);
    onLoadedMetadata();
  }, [onLoadedMetadata]);

  const onErrorHandlerInternal = useCallback((error) => {
    setVideoLoadState(VideoLoadState.error);
    onErrorHandler(error);
  }, [onErrorHandler]);

  return (
    <div className={classnames(classes.container, 'video-load-preview')}>
      {
        videoLoadState === VideoLoadState.notLoaded &&
        <ItemPlaceholderBoxView
          className={classes.placeholderView}
          mainViewIcon={videoIcon}
          mainViewIconLabel={i18n.getString(_TL_('Video'))}
          footerProps={footerProps}
        />
      }
      <div className={classnames(classes.hidden, videoLoadState === VideoLoadState.loaded && classes.shown)}>
        <VideoPlaybackView
          onLoadedMetadata={onLoadedMetadataInternal}
          onErrorHanler={onErrorHandlerInternal}
          src={url}
          height={400}
          isOutlineGray
        />
      </div>
    </div>
  );
};

VideoLoadPreview.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  url: PropTypes.string.isRequired,
  onLoadedMetadata: PropTypes.func,
  onErrorHandler: PropTypes.func,
};

VideoLoadPreview.defaultProps = {
  onLoadedMetadata: _.noop,
  onErrorHandler: _.noop,
};
